import { DevAuthProvider } from './dev-auth';
import { IdentityPlatformAuthProvider } from './identity-platform';
import type { AuthProvider } from './types';

export * from './types';
export * from './errors';

/**
 * `VITE_FIREBASE_AUTH_DOMAIN=self` means "this app's own host": nginx proxies
 * /__/ to <project>.firebaseapp.com so the redirect sign-in stays same-origin
 * (browsers partition third-party storage). Native apps have no proxy, so they
 * use <project>.firebaseapp.com.
 */
export function resolveAuthDomain(configured: string | undefined, projectId: string, host: string, native: boolean): string {
  if (configured && configured !== 'self' && configured !== `${projectId}.firebaseapp.com`) return configured;
  return native || !host ? `${projectId}.firebaseapp.com` : host;
}

/** Public web origin hosting `/native-auth` for the native store apps' system-browser OAuth flow. */
export function resolveWebOrigin(configuredWebUrl: string | undefined, apiUrl: string | undefined, origin: string, native: boolean): string {
  if (configuredWebUrl) return configuredWebUrl.replace(/\/+$/, '');
  if (!native && origin) return origin.replace(/\/+$/, '');
  if (apiUrl) {
    try {
      return new URL(apiUrl).origin.replace(/-api\./, '.');
    } catch {
      return apiUrl.replace(/\/api\/v\d+.*$/, '').replace(/\/+$/, '').replace(/-api\./, '.');
    }
  }
  return 'https://perimeteriq.g4s.com';
}

export function createAuthProvider(env: ImportMetaEnv = import.meta.env): AuthProvider {
  if ((env.VITE_AUTH_MODE ?? 'dev') === 'identity-platform') {
    const projectId = env.VITE_GCP_PROJECT_ID ?? '';
    const native = !!(globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();
    const apiUrl = (env.VITE_API_URL ?? '').replace(/\/+$/, '');
    return new IdentityPlatformAuthProvider({
      apiKey: env.VITE_IDP_API_KEY ?? '',
      authDomain: resolveAuthDomain(env.VITE_FIREBASE_AUTH_DOMAIN, projectId, globalThis.location?.host ?? '', native),
      projectId,
      workspaceDomain: env.VITE_WORKSPACE_DOMAIN ?? 'g4s.com',
      appName: env.VITE_APP_NAME ?? 'SSS',
      apiUrl,
      webOrigin: resolveWebOrigin(env.VITE_WEB_URL, apiUrl, globalThis.location?.origin ?? '', native),
    });
  }
  return new DevAuthProvider();
}
